/**
 * Pure value helpers backing the eCOS list field (ticket #23442).
 *
 * The legacy relevé stores an attribute's value as a COMMA-JOINED STRING of option ids —
 * `"12"` for a simple list (attribute type 4), `"12,15,18"` for a multiple one (type 5) — on
 * `shelfStatementProductModel.attributes[]`. Keeping the parsing, the ordering and the chip
 * split out of the component means those edge cases are unit-testable without mounting Vuetify,
 * which matters because this repo has no component/DOM test setup at all.
 *
 * Ids are treated as OPAQUE STRINGS throughout. The legacy option ids happen to be numeric today,
 * but RG-09 says the component may assume nothing about the option content, and coercing to Number
 * would silently corrupt any non-numeric id.
 */

/** @param {unknown} id @returns {string} */
const asKey = (id) => (id === null || id === undefined ? '' : String(id).trim());

/**
 * Splits the legacy comma-joined string into option ids.
 *
 * Tolerates everything the legacy actually produces: `''` when nothing is selected, a trailing
 * comma, and doubled separators (`__attributeChanged` builds the string with `.join(',')` over a
 * `:checked` scan, but older stored values are not guaranteed clean).
 *
 * @param {string|number|null|undefined} raw
 * @returns {string[]} ids, de-duplicated, in the order they appeared
 */
export function parseLegacyValue(raw) {
	if (raw === null || raw === undefined || raw === '') return [];

	const seen = new Set();
	for (const part of String(raw).split(',')) {
		const key = asKey(part);
		if (key !== '') seen.add(key);
	}

	return [...seen];
}

/**
 * Renders option ids back into the legacy comma-joined string.
 *
 * @param {Array<string|number>} values
 * @returns {string} `''` when nothing is selected — the legacy's own "empty" representation
 */
export function formatLegacyValue(values) {
	if (!Array.isArray(values)) return '';

	const seen = new Set();
	for (const value of values) {
		const key = asKey(value);
		if (key !== '') seen.add(key);
	}

	return [...seen].join(',');
}

/**
 * Reduces any accepted value shape to the ids that actually exist, in CONFIGURATION order.
 *
 * RG-05: the option order is the field's configuration, so the selection is re-ordered to match
 * `options` rather than kept in click order or sorted. Ids absent from `options` are dropped —
 * a stale stored value must not render as a chip with no label.
 *
 * @param {Array<string|number>|string|number|null|undefined} value array, scalar, or legacy string
 * @param {{ multiple?: boolean, options?: Array<object>, itemValue?: string }} config
 * @returns {string[]}
 */
export function normalizeSelection(value, { multiple = false, options = [], itemValue = 'id' } = {}) {
	let requested;
	if (Array.isArray(value)) requested = value.map(asKey).filter((key) => key !== '');
	else if (typeof value === 'string') requested = parseLegacyValue(value);
	else requested = parseLegacyValue(value);

	const wanted = new Set(requested);
	// Walking `options` rather than `requested` is what enforces config order in one pass.
	const ordered = [];
	for (const option of options || []) {
		const key = asKey(option?.[itemValue]);
		if (key !== '' && wanted.has(key)) ordered.push(key);
	}

	return multiple ? ordered : ordered.slice(0, 1);
}

/**
 * Applies a click on one option.
 *
 * Simple mode is exclusive, and re-clicking the already-selected option is a no-op rather than a
 * clear: silently emptying a stored value on a mis-tap is the kind of data loss the relevé cannot
 * afford, and clearing has its own affordance.
 *
 * @param {Array<string|number>} current
 * @param {string|number} id
 * @param {{ multiple?: boolean, options?: Array<object>, itemValue?: string }} config
 * @returns {string[]} the next selection, in configuration order
 */
export function toggleSelection(current, id, { multiple = false, options = [], itemValue = 'id' } = {}) {
	const key = asKey(id);
	if (key === '') return normalizeSelection(current, { multiple, options, itemValue });
	if (!multiple) return normalizeSelection([key], { multiple, options, itemValue });

	const selected = new Set(normalizeSelection(current, { multiple, options, itemValue }));
	if (selected.has(key)) selected.delete(key);
	else selected.add(key);

	return normalizeSelection([...selected], { multiple, options, itemValue });
}

/**
 * Computes everything the cell renders (RG-01, RG-02, RG-03, RG-06, CL-02, CL-04).
 *
 * `maxDisplayedChips` is a budget, not a cap on the selection: the surplus collapses into a single
 * « +N » chip. Note there is deliberately no `+0` chip when the selection exactly fills the budget.
 *
 * @param {Array<string|number>} selected
 * @param {Array<object>} options
 * @param {{ multiple?: boolean, maxDisplayedChips?: number, itemValue?: string, titleKey?: string, translate?: ((key: string) => string)|null }} config
 * @returns {{ isEmpty: boolean, text: string, chips: Array<{ id: string, label: string }>, overflowCount: number, title: string }}
 */
export function buildDisplay(selected, options, { multiple = false, maxDisplayedChips = 2, itemValue = 'id', titleKey = 'name', translate = null } = {}) {
	const labelOf = (option) => {
		const raw = option?.[titleKey];
		const label = raw === null || raw === undefined ? '' : String(raw);
		return translate ? translate(label) : label;
	};

	const byId = new Map();
	for (const option of options || []) {
		const key = asKey(option?.[itemValue]);
		if (key !== '') byId.set(key, labelOf(option));
	}

	// Unknown ids are skipped entirely — they must not surface as a blank chip, and must not
	// inflate the overflow count either, or the cell would claim values the user cannot see.
	const resolved = normalizeSelection(selected, { multiple, options, itemValue }).map((id) => ({ id, label: byId.get(id) ?? '' }));

	if (resolved.length === 0) {
		return { isEmpty: true, text: '', chips: [], overflowCount: 0, title: '' };
	}

	const title = resolved.map((entry) => entry.label).join(', ');

	// RG-02: a single value reads as plain text, not as a chip.
	if (!multiple) {
		return { isEmpty: false, text: resolved[0].label, chips: [], overflowCount: 0, title };
	}

	const budget = Math.max(0, Number(maxDisplayedChips) || 0);

	return { isEmpty: false, text: '', chips: resolved.slice(0, budget), overflowCount: Math.max(0, resolved.length - budget), title };
}
